import { useState, useEffect, useRef } from "react";
import {
  Home,
  BookOpen,
  Search,
  Sparkles,
  Star,
  ArrowDownToLine,
  User,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
} from "lucide-react";
import "./FeatureShowcase.css";

interface FeatureItem {
  id: string;
  shortName: string;
  badge: string;
  headline: string;
  description: string;
  image: string;
  alt: string;
  highlights: string[];
  icon: typeof Home;
}

const features: FeatureItem[] = [
  {
    id: "home-feed",
    shortName: "Home Feed",
    badge: "01 · Home & Community",
    headline: "Your Reading, Front & Center",
    description:
      "See what you're currently reading, track your page progress, and stay connected with what your fellow book lovers are up to — all from one cozy home feed.",
    image: "/images/home-screen.jpg",
    alt: "Bookshelf Home Feed showing current reads and community activity",
    highlights: ["Live page tracking", "Community friend activity", "Quick update shortcuts"],
    icon: Home,
  },
  {
    id: "library",
    shortName: "My Library",
    badge: "02 · Personal Shelves",
    headline: "Build Your Perfect Bookshelf",
    description:
      "Organize your reads into custom shelves — Want to Read, Currently Reading, Finished, or any collection that matches your mood and style.",
    image: "/images/library-screen.jpg",
    alt: "Bookshelf Library Screen showing customized shelves and book collections",
    highlights: ["Custom categorized shelves", "Grid & list layouts", "Progress-based grouping"],
    icon: BookOpen,
  },
  {
    id: "search",
    shortName: "Search & Vibes",
    badge: "03 · Smart Discovery",
    headline: "Find Your Next Favorite",
    description:
      "Search by title, author, genre, or even mood. Results show which books are already in your library, so you never lose track.",
    image: "/images/search-screen.jpg",
    alt: "Bookshelf Search Screen with genre and mood filters",
    highlights: ["Filter by mood & theme", "In-library indicator badges", "Instant author & title search"],
    icon: Search,
  },
  {
    id: "book-details",
    shortName: "Book Details",
    badge: "04 · Deep Dives",
    headline: "Know Before You Read",
    description:
      "Get the full picture — synopsis, page count, publication info, and community ratings — before you commit to your next read.",
    image: "/images/book-detail-screen.jpg",
    alt: "Bookshelf Book Details Screen with rich metadata and synopsis",
    highlights: ["Comprehensive synopses", "Edition and page counts", "Community rating distribution"],
    icon: Sparkles,
  },
  {
    id: "reviews",
    shortName: "Reviews",
    badge: "05 · Reader Reflections",
    headline: "Share What Moved You",
    description:
      "Rate books, write thoughtful reviews, and help fellow readers discover stories worth their time.",
    image: "/images/book-review-screen.jpg",
    alt: "Bookshelf Book Review Screen with star ratings and review entry",
    highlights: ["Star ratings & reflections", "Expressive reader notes", "Community discussion"],
    icon: Star,
  },
  {
    id: "goodreads-import",
    shortName: "Goodreads Sync",
    badge: "06 · 1-Tap Migration",
    headline: "Switch Without Starting Over",
    description:
      "Import your entire Goodreads library in one tap — shelves, ratings, and reading history come along for the ride.",
    image: "/images/goodreads-import-screen.jpg",
    alt: "Bookshelf Goodreads Import Screen for seamless library transfer",
    highlights: ["Full history migration", "Preserve ratings & dates", "One-click effortless sync"],
    icon: ArrowDownToLine,
  },
  {
    id: "profile",
    shortName: "Reader Profile",
    badge: "07 · Reading Goals",
    headline: "Your Reading Journey, Visualized",
    description:
      "Track books read, pages turned, and reading streaks. Set yearly goals and watch your progress grow.",
    image: "/images/profile-screen.jpg",
    alt: "Bookshelf Profile Screen with streak tracking and reading goals",
    highlights: ["Reading streaks counter", "Annual book goal tracker", "All-time pages logged"],
    icon: User,
  },
  {
    id: "reading-stats",
    shortName: "Insights & Stats",
    badge: "08 · Deep Analytics",
    headline: "Dive Into Your Reading Stats",
    description:
      "Discover your reading speed, longest reads, and patterns over time. Every page turned tells a story about you as a reader.",
    image: "/images/reading-stats-screen.jpg",
    alt: "Bookshelf Reading Stats Screen with detailed metrics and reading pace",
    highlights: ["Average reading speed", "Longest & shortest books", "Reading pace trends"],
    icon: BarChart3,
  },
];

interface PhoneFrameProps {
  src: string;
  alt: string;
  isFirst?: boolean;
}

function PhoneFrame({ src, alt, isFirst = false }: PhoneFrameProps) {
  return (
    <div className="relative mx-auto flex items-center justify-center select-none w-full max-w-[260px] xs:max-w-[280px] sm:max-w-[320px] md:max-w-[340px] lg:max-w-[360px]">
      {/* Soft warm ambient glow behind the frame */}
      <div className="absolute -inset-4 bg-gradient-to-br from-accent/20 via-primary-light/15 to-transparent rounded-[3rem] blur-2xl -z-10 opacity-70 pointer-events-none" />

      {/* Outer device bezel */}
      <div className="phone-mockup-frame relative w-full aspect-[1440/2795] bg-slate-900 border-[6px] sm:border-[8px] border-gray-200/95 rounded-[2.5rem] sm:rounded-[2.8rem] overflow-hidden flex flex-col">
        {/* Screen image container */}
        <div className="relative w-full h-full rounded-[2rem] sm:rounded-[2.3rem] overflow-hidden bg-white">
          <img
            src={src}
            alt={alt}
            width={1440}
            height={2795}
            loading={isFirst ? "eager" : "lazy"}
            {...(isFirst ? ({ fetchPriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>) : {})}
            className="w-full h-full object-cover object-top block"
          />

          {/* Premium screen glass glare overlay */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/12 pointer-events-none z-20" />
        </div>
      </div>
    </div>
  );
}

export default function ReadingExperience() {
  const [activeTab, setActiveTab] = useState(0);
  const tabListRef = useRef<HTMLDivElement>(null);
  const activeFeature = features[activeTab];
  const ActiveIcon = activeFeature.icon;

  // Scroll-triggered animations for mobile & tablet sections
  useEffect(() => {
    const isReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (isReducedMotion) {
      document.querySelectorAll(".feature-reveal").forEach((el) => {
        el.classList.add("is-visible");
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );

    const elements = document.querySelectorAll(".feature-reveal");
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handlePrevTab = () => {
    setActiveTab((prev) => (prev > 0 ? prev - 1 : features.length - 1));
  };

  const handleNextTab = () => {
    setActiveTab((prev) => (prev < features.length - 1 ? prev + 1 : 0));
  };

  return (
    <section id="features" className="bg-experience-bg py-16 sm:py-20 md:py-24 relative overflow-hidden">
      {/* Invisible anchor so `#vision` links also scroll to this showcase */}
      <div id="vision" className="scroll-mt-28" />

      {/* Section Header */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center mb-12 sm:mb-16 md:mb-20">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 tracking-tight mb-4">
          A Reading Experience Designed for How You Feel
        </h2>
        <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
          Bookshelf isn't just about cataloging books—it's about honoring the emotions, memories, and personal connections you make with every story you read.
        </p>
      </div>

      {/* ========================================================= */}
      {/* DESKTOP VIEW (≥ lg): Sticky Tab Selector + Interactive Showcase */}
      {/* ========================================================= */}
      <div className="hidden lg:block max-w-7xl mx-auto px-6">
        {/* Sticky Tab Selector */}
        <div className="sticky top-[72px] z-30 py-3 mb-10 bg-experience-bg/95 backdrop-blur-md border-y border-accent-light/50">
          <div
            ref={tabListRef}
            role="tablist"
            aria-label="Bookshelf App Features"
            className="w-full overflow-x-auto no-scrollbar px-4 sm:px-6 py-1.5"
          >
            <div className="flex items-center gap-2.5 min-w-max mx-auto justify-start xl:justify-center">
              {features.map((feature, idx) => {
                const IconComp = feature.icon;
                const isActive = activeTab === idx;
                return (
                  <button
                    key={feature.id}
                    role="tab"
                    id={`tab-${feature.id}`}
                    aria-selected={isActive}
                    aria-controls={`panel-${feature.id}`}
                    onClick={() => setActiveTab(idx)}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs xl:text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap shrink-0 ${isActive
                        ? "bg-accent text-white shadow-md font-semibold ring-2 ring-accent/30"
                        : "bg-white hover:bg-accent-light/40 text-gray-700 hover:text-accent border border-gray-200/90 shadow-sm"
                      }`}
                  >
                    <IconComp className={`w-4 h-4 shrink-0 ${isActive ? "text-white" : "text-accent"}`} />
                    <span>{feature.shortName}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Desktop Showcase Panel */}
        <div
          id={`panel-${activeFeature.id}`}
          role="tabpanel"
          aria-labelledby={`tab-${activeFeature.id}`}
          className="tab-content-animate bg-white/60 border border-gray-200/80 rounded-3xl p-8 xl:p-12 shadow-sm grid grid-cols-12 gap-10 items-center"
          key={activeFeature.id}
        >
          {/* Narrative Column */}
          <div className="col-span-7 flex flex-col justify-center space-y-6 pr-4">
            <div className="inline-flex items-center gap-2 self-start bg-accent-light/70 border border-accent/20 text-accent text-xs font-semibold uppercase tracking-wider px-3.5 py-1.5 rounded-full">
              <ActiveIcon className="w-3.5 h-3.5" />
              <span>{activeFeature.badge}</span>
            </div>

            <h3 className="text-3xl xl:text-4xl font-bold text-gray-900 leading-tight">
              {activeFeature.headline}
            </h3>

            <p className="text-lg text-gray-600 leading-relaxed">
              {activeFeature.description}
            </p>

            {/* Key feature bullets */}
            <div className="flex flex-wrap gap-2.5 pt-2">
              {activeFeature.highlights.map((highlight, idx) => (
                <div
                  key={idx}
                  className="inline-flex items-center gap-2 bg-white border border-gray-200 px-3.5 py-2 rounded-xl text-sm font-medium text-gray-700 shadow-sm"
                >
                  <span className="w-2 h-2 rounded-full bg-accent" />
                  <span>{highlight}</span>
                </div>
              ))}
            </div>

            {/* Actions & Step Navigation */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200/70">
              <a
                href="#beta-list"
                className="inline-flex items-center gap-2 text-accent hover:text-primary-light font-semibold text-base transition group"
              >
                <span>Join the beta waitlist</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>

              {/* Prev / Next controls */}
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 font-medium">
                  {activeTab + 1} of {features.length}
                </span>
                <button
                  onClick={handlePrevTab}
                  aria-label="Previous feature"
                  className="w-8 h-8 rounded-full border border-gray-300 bg-white hover:bg-gray-100 flex items-center justify-center text-gray-700 transition"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={handleNextTab}
                  aria-label="Next feature"
                  className="w-8 h-8 rounded-full border border-gray-300 bg-white hover:bg-gray-100 flex items-center justify-center text-gray-700 transition"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Device Showcase Column */}
          <div className="col-span-5 flex justify-center items-center">
            <PhoneFrame
              src={activeFeature.image}
              alt={activeFeature.alt}
              isFirst={activeTab === 0}
            />
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* MOBILE & TABLET VIEW (< lg): Zig-Zag Alternating Showcase */}
      {/* ========================================================= */}
      <div className="lg:hidden max-w-5xl mx-auto px-4 sm:px-6 md:px-8 space-y-12 sm:space-y-16 md:space-y-28">
        {features.map((feature, idx) => {
          const IconComp = feature.icon;
          const isOdd = idx % 2 === 1;

          return (
            <div
              key={feature.id}
              className={`feature-reveal flex flex-col md:flex-row items-center gap-8 md:gap-12 lg:gap-16 p-6 sm:p-8 md:p-0 bg-white/40 md:bg-transparent border border-accent-light/60 md:border-0 rounded-3xl md:rounded-none shadow-sm md:shadow-none ${isOdd ? "md:flex-row-reverse" : ""
                }`}
            >
              {/* Narrative Content (First on mobile so users read what they're looking at) */}
              <div className="w-full md:w-1/2 flex flex-col justify-center text-center md:text-left space-y-4 max-w-md mx-auto md:mx-0">
                <div className="inline-flex items-center gap-2 self-center md:self-start bg-accent-light/70 border border-accent/20 text-accent text-xs font-semibold uppercase tracking-wider px-3.5 py-1.5 rounded-full">
                  <IconComp className="w-3.5 h-3.5" />
                  <span>{feature.badge}</span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
                  {feature.headline}
                </h3>

                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {feature.description}
                </p>

                {/* Highlight tags */}
                <div className="flex flex-wrap gap-2 justify-center md:justify-start pt-1">
                  {feature.highlights.map((highlight, hIdx) => (
                    <span
                      key={hIdx}
                      className="inline-flex items-center gap-1.5 text-xs text-gray-700 bg-white border border-gray-200/80 px-2.5 py-1 rounded-lg shadow-sm"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                      {highlight}
                    </span>
                  ))}
                </div>
              </div>

              {/* Device Frame */}
              <div className="w-full md:w-1/2 flex justify-center">
                <PhoneFrame
                  src={feature.image}
                  alt={feature.alt}
                  isFirst={idx === 0}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
